/**
 * Every event the web app reports, by name.
 *
 * Names are `snake_case`, `object_verb`, past tense. The catalogue is a contract with mobile
 * and the backend, which keep their own copies of the same names (no cross-app imports in
 * this repo). The source of truth for all three is resource/docs/plans/posthog-event-map.md —
 * change it there first.
 */
export type AnalyticsEventDef = Readonly<{ name: string }>

export const AnalyticsEvent = {
  // Engagement. Standard and programme workouts share one event, told apart by `is_programme`.
  WORKOUT_STARTED: { name: 'workout_started' },
  WORKOUT_COMPLETED: { name: 'workout_completed' },
  PROGRAMME_STARTED: { name: 'programme_started' },
  PROGRAMME_COMPLETED: { name: 'programme_completed' },
  PROGRAMME_CANCELLED: { name: 'programme_cancelled' },

  // Monetisation, client side. Funnels, not revenue — the RevenueCat webhook on the backend is
  // the ledger. On web the purchase runs through Stripe Checkout: `purchase_started` is the
  // click that leaves for Stripe, `purchase_completed` the return to `/profile/personal/success`.
  PAYWALL_VIEWED: { name: 'paywall_viewed' },
  PURCHASE_STARTED: { name: 'purchase_started' },
  PURCHASE_COMPLETED: { name: 'purchase_completed' },

  // Auth. `signup_completed` vs `login_completed` is the backend's call (`isNewUser` on the
  // auth responses).
  SIGNUP_STARTED: { name: 'signup_started' },
  OTP_REQUESTED: { name: 'otp_requested' },
  OTP_RESENT: { name: 'otp_resent' },
  OTP_VERIFIED: { name: 'otp_verified' },
  OTP_FAILED: { name: 'otp_failed' },
  SIGNUP_COMPLETED: { name: 'signup_completed' },
  LOGIN_COMPLETED: { name: 'login_completed' },
  AUTH_FAILED: { name: 'auth_failed' },
  LOGGED_OUT: { name: 'logged_out' },

  // App tour.
  APP_TOUR_STARTED: { name: 'app_tour_started' },
  APP_TOUR_COMPLETED: { name: 'app_tour_completed' },
  APP_TOUR_DISMISSED: { name: 'app_tour_dismissed' },

  // Featured (free) workouts — GH-861. Shared with mobile; the call sites pass the contract's
  // camelCase property names and `analyticsService` snake_cases them on the way out.
  FEATURED_SHELF_VIEWED: { name: 'featured_shelf_viewed' },
  FEATURED_WORKOUT_PLAY_CLICKED: { name: 'featured_workout_play_clicked' },
  FEATURED_WORKOUT_PREVIEW_STARTED: { name: 'featured_workout_preview_started' },
  FEATURED_WORKOUT_STARTED: { name: 'featured_workout_started' },
  FEATURED_WORKOUT_COMPLETED: { name: 'featured_workout_completed' },
  FEATURED_WORKOUT_ENDED: { name: 'featured_workout_ended' },
  FEATURED_WORKOUT_PLAY_FAILED: { name: 'featured_workout_play_failed' },
  FEATURED_UPSELL_SHOWN: { name: 'featured_upsell_shown' },
} as const satisfies Record<string, AnalyticsEventDef>
